import { FC, useEffect, useMemo, useState } from "react";
import { Stack, useTheme, Box } from "@mui/material";
import {
  FormikCheckbox,
  FormikSelectField,
  FormikToggleField,
  TableToggleField,
  DateRangePicker
} from "@vilocnv/allsetra-core";
import {
  filterObjectsByGroup,
  mapKeysWithDisplayLabel,
  removeDuplicateObjects,
  supportsReportInactiveObjectsToggle
} from "app/data/helpers";
import TimeFilters from "../../TimeFilters/TimeFilters";
import DaysFilters from "../../DaysFilter/DaysFilter";
import { RIDE_MODES } from "app/data/constants";
import { isEmpty } from "lodash";
import { FormLabelText, LabelRowBox } from "./InnerForm.styled";
import { useTranslation } from "react-i18next";

interface Values {
  group: string[];
  object: string[];
  objectIds?: string[];
  assignedToAllObjects: boolean;
}

interface Props {
  groups: Array<any>;
  objectTypes: Array<any>;
  objects: Array<any>;
  keys: Array<any>;
  dataLoading?: boolean;
  translator: any;
  fetchOnDebounce: (value: string) => void;
  onChange?: (val?: any) => void;
  values: Values;
  setFieldValue: any;
  isCO2InnerForm?: boolean;
  isMultipleRidesForm?: boolean;
  isMileageReport?: boolean;
  reportType?: number;
  users?: Array<any>;
  showDateRangePicker?: boolean;
  dateValues?: any;
  handleDateChange?: (value: any) => void;
  dateRangeLoading?: boolean;
  driverIsRequired?: boolean;
}

const InnerForm: FC<Props> = ({
  groups,
  objectTypes,
  objects,
  keys,
  dataLoading = false,
  translator,
  fetchOnDebounce: _fetchOnDebounce,
  onChange,
  values,
  setFieldValue,
  isCO2InnerForm = false,
  isMultipleRidesForm = false,
  isMileageReport = false,
  reportType = 2,
  users,
  showDateRangePicker = false,
  dateValues,
  handleDateChange,
  dateRangeLoading = false,
  driverIsRequired = true
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isSelectDaysAndTimeEnable, setIsSelectDaysAndTimeEnable] =
    useState(false);
  const [prevAssignedToAllObjects, setPrevAssignedToAllObjects] = useState(
    values?.assignedToAllObjects
  );

  const keyOptions = useMemo(() => mapKeysWithDisplayLabel(keys), [keys]);

  const handleToggleDaysAndTimeChange = (value: boolean) => {
    setIsSelectDaysAndTimeEnable(value);
  };

  const objectFieldName = reportType === 8 ? "objectIds" : "object";

  useEffect(() => {
    // Only reconcile object selection when a group filter is actively applied.
    // Never drop selected IDs just because options are still loading/incomplete.
    if (!values || values[objectFieldName] === undefined) return;
    if (!values.group?.length || reportType === 8) return;
    if (!objects?.length) return;

    const filteredObjects = filterObjectsByGroup(values, objects, groups);
    const uniqueFilteredObjects = removeDuplicateObjects(filteredObjects);
    const allowedIds = new Set(
      uniqueFilteredObjects.map((item) => item.uniqueId)
    );
    const knownOptionIds = new Set(objects.map((item) => item.uniqueId));
    const current = values[objectFieldName] || [];
    // Keep IDs not yet present in options (still loading); only drop known IDs
    // that fall outside the active group filter.
    const newObjectsValues = current.filter((id: string) => {
      if (!knownOptionIds.has(id)) return true;
      return allowedIds.has(id);
    });

    if (
      current.length === newObjectsValues.length &&
      current.every(
        (id: string, index: number) => id === newObjectsValues[index]
      )
    ) {
      return;
    }

    setFieldValue(objectFieldName, newObjectsValues);
  }, [values.group, objects, groups, reportType, objectFieldName]);

  useEffect(() => {
    if (values?.assignedToAllObjects === prevAssignedToAllObjects) {
      return;
    }

    if (values.assignedToAllObjects) {
      setFieldValue(objectFieldName, []);
      // Pass [] so selectedLocalIds is cleared (onChange() without args also clears)
      onChange?.([]);
    }

    setPrevAssignedToAllObjects(values.assignedToAllObjects);
  }, [values.assignedToAllObjects]);

  return (
    <Stack spacing={2}>
      {isMileageReport ? (
        <FormikSelectField
          theme={theme}
          label={translator("rideMode", { ns: "formFieldsTranslation" })}
          placeholder={translator("rideMode", { ns: "formFieldsTranslation" })}
          name="rideModes"
          options={RIDE_MODES(translator)}
          optionLabelKey="name"
          optionValueKey="value"
          multiple
          searchable
          fullWidth
        />
      ) : null}

      {showDateRangePicker && (
        <>
          <FormLabelText>{t("reports.selectDateRange")}</FormLabelText>
          <Box
            sx={{
              marginTop: "8px !important",
              marginBottom: "8px !important"
            }}
          >
            <DateRangePicker
              name="dateRange"
              onChange={(value) => {
                setFieldValue("dateRange", value);
                if (handleDateChange) {
                  handleDateChange(value);
                }
              }}
              value={dateValues}
              disabled={dateRangeLoading}
              theme={theme}
              translator={t}
              disableFutureDates
            />
          </Box>
        </>
      )}

      <FormikCheckbox
        theme={theme}
        name="assignedToAllObjects"
        label={translator("selectAllObjects.label", {
          ns: "formFieldsTranslation"
        })}
      />

      {!values.assignedToAllObjects && (
        <FormikSelectField
          theme={theme}
          label={translator("objects.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("objects.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name={objectFieldName}
          options={
            reportType === 8
              ? objects
              : (filterObjectsByGroup(values, objects, groups) ?? [])
          }
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          onChange={onChange}
          required={isMultipleRidesForm}
          fullWidth
          sx={{
            marginTop: "8px !important",
            "& .MuiAutocomplete-hasClearIcon.css-1x6bjyf-MuiAutocomplete-root .MuiOutlinedInput-root":
              {
                paddingRight: "9px"
              }
          }}
          //@ts-ignore
          showStates
          showDropdownIcon
        />
      )}

      {reportType === 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("driver.label", {
            ns: "formFieldsTranslation"
          })}
          placeholder={translator("driver.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="driverId"
          options={users ?? []}
          optionLabelKey="email"
          optionValueKey="uniqueId"
          searchable
          onChange={onChange}
          required={driverIsRequired}
          fullWidth
        />
      )}

      {reportType !== 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("objectType.label", {
            ns: "formFieldsTranslation"
          })}
          placeholder={translator("objectType.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="objectType"
          options={objectTypes ?? []}
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      )}

      {groups?.length && reportType !== 8 ? (
        <FormikSelectField
          theme={theme}
          label={translator("groups.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("groups.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="group"
          options={groups ?? []}
          optionLabelKey="name"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      ) : (
        <></>
      )}
      {!isEmpty(keys) && reportType !== 8 && (
        <FormikSelectField
          theme={theme}
          label={translator("keys.label", { ns: "formFieldsTranslation" })}
          placeholder={translator("keys.placeholder", {
            ns: "formFieldsTranslation"
          })}
          name="key"
          options={keyOptions}
          optionLabelKey="displayLabel"
          optionValueKey="uniqueId"
          loading={dataLoading}
          multiple
          searchable
          fullWidth
        />
      )}

      {reportType !== 8 && (
        <TableToggleField
          label={
            translator("common.selectDaysAndTime") ?? "Select days and time"
          }
          name="selectDaysAndTime"
          onChange={(e) => handleToggleDaysAndTimeChange(e.target.checked)}
          checked={isSelectDaysAndTimeEnable}
        />
      )}

      {isSelectDaysAndTimeEnable && (
        <>
          <DaysFilters values={values} />

          <TimeFilters values={values} />
        </>
      )}

      {supportsReportInactiveObjectsToggle(reportType) &&
        !isMultipleRidesForm && (
          <FormikToggleField
            name="showInactiveObjects"
            label={
              translator("common.showInactiveObjects") ||
              "Show inactive objects"
            }
          />
        )}

      {/* {!isCO2InnerForm && <DateAndTimeFilters values={values} />} */}

      {/* TODO will be aded later  */}
      {/* {isCO2InnerForm && (
        <>
          <RideModeFilters />
          <FormikToggleField
            label={translator("excludeRides.label", {
              ns: "formFieldsTranslation",
            })}
            name="excludeRides"
          />
        </>
      )} */}
    </Stack>
  );
};

export default InnerForm;
